import React from "react";

import {
  useGetTenantPaymentSettings,
  useUpdateTenantPaymentSettings,
} from "./hooks/tenant-payments.hooks";
import {
  useGetTenantPartnersSettings,
  useUpdateTenantPartnersSettings,
} from "./hooks/tenant-partners.hooks";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { BillingPageState } from "../billing-settings/components/BillingPageState";
import { BillingEnabledRow } from "../billing-settings/components/BillingEnableRow";
import { AdminPmsSettingsSection } from "./components/AdminPmsSettingsSection";
import { PartnersIntegrationEnabledRow } from "./components/PartnersIntegrationEnabledRow";
import { TenantProfileSection } from "@/components/pages/admin-settings/components/TenantProfileSection";
import { TenantHotelsStripeSection } from "@/components/pages/admin-settings/components/TenantHotelsStripeSection";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { useTenantPermissions } from "@/hooks/useTenantPermissions";
import { isTenantAdminRole } from "@/utils/accessControl";
import { getDefaultAuthorizedRoute } from "@/utils/defaultAuthorizedRoute";

interface SectionAccess {
  canRead: boolean;
  canWrite: boolean;
}

interface ResolveSectionAccessOptions {
  isTenantAdmin: boolean;
  hasTenantPermissions: boolean;
  hasPlatformPermissions: boolean;
  resource: "settings" | "billing" | "partners";
  canReadTenant: (resource: string) => boolean;
  canAdministerTenant: (resource: string) => boolean;
  canReadPlatform: (resource: string) => boolean;
  canAdministerPlatform: (resource: string) => boolean;
}

interface SettingsToggleSectionProps {
  checked: boolean;
  disabled: boolean;
  isLoading: boolean;
  isError: boolean;
  loadingText: string;
  errorText: string;
  renderToggle: (
    checked: boolean,
    disabled: boolean,
    onChange: (checked: boolean) => void,
  ) => React.ReactNode;
  onChange: (checked: boolean) => void;
}

function resolveSectionAccess({
  isTenantAdmin,
  hasTenantPermissions,
  hasPlatformPermissions,
  resource,
  canReadTenant,
  canAdministerTenant,
  canReadPlatform,
  canAdministerPlatform,
}: ResolveSectionAccessOptions): SectionAccess {
  if (isTenantAdmin) {
    const canManageAllSettings =
      hasTenantPermissions && canAdministerTenant("settings");
    const canViewAllSettings =
      hasTenantPermissions && canReadTenant("settings");

    return {
      canRead:
        !hasTenantPermissions ||
        canViewAllSettings ||
        canManageAllSettings ||
        canReadTenant(resource),
      canWrite:
        !hasTenantPermissions ||
        canManageAllSettings ||
        canAdministerTenant(resource),
    };
  }

  return {
    canRead: !hasPlatformPermissions || canReadPlatform("settings"),
    canWrite: !hasPlatformPermissions || canAdministerPlatform("settings"),
  };
}

function SettingsToggleSection({
  checked,
  disabled,
  isLoading,
  isError,
  loadingText,
  errorText,
  renderToggle,
  onChange,
}: SettingsToggleSectionProps): React.ReactNode {
  if (isLoading) {
    return <BillingPageState text={loadingText} />;
  }

  if (isError) {
    return <BillingPageState text={errorText} tone="error" />;
  }

  return renderToggle(checked, disabled, onChange);
}

export const AdminSettingsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { userProfile, isLoading: isUserLoading } = useAuthenticatedUser();
  const { hasPlatformPermissions, canRead, canAdminister } =
    usePlatformPermissions();
  const {
    hasTenantPermissions,
    canRead: canReadTenant,
    canAdminister: canAdministerTenant,
  } = useTenantPermissions();
  const isTenantAdmin = isTenantAdminRole(userProfile);
  const settingsAccess = resolveSectionAccess({
    isTenantAdmin,
    hasTenantPermissions,
    hasPlatformPermissions,
    resource: "settings",
    canReadTenant,
    canAdministerTenant,
    canReadPlatform: canRead,
    canAdministerPlatform: canAdminister,
  });
  const billingAccess = resolveSectionAccess({
    isTenantAdmin,
    hasTenantPermissions,
    hasPlatformPermissions,
    resource: "billing",
    canReadTenant,
    canAdministerTenant,
    canReadPlatform: canRead,
    canAdministerPlatform: canAdminister,
  });
  const partnersAccess = resolveSectionAccess({
    isTenantAdmin,
    hasTenantPermissions,
    hasPlatformPermissions,
    resource: "partners",
    canReadTenant,
    canAdministerTenant,
    canReadPlatform: canRead,
    canAdministerPlatform: canAdminister,
  });
  const canReadAnySection =
    settingsAccess.canRead || billingAccess.canRead || partnersAccess.canRead;

  const {
    data: tenantPaymentSettings,
    isLoading,
    isError,
  } = useGetTenantPaymentSettings(billingAccess.canRead);
  const updateTenantPaymentSettings = useUpdateTenantPaymentSettings();

  const {
    data: tenantPartnersSettings,
    isLoading: isPartnersLoading,
    isError: isPartnersError,
  } = useGetTenantPartnersSettings(partnersAccess.canRead);
  const updateTenantPartnersSettings = useUpdateTenantPartnersSettings();
  const handlePaymentToggleError = () => {
    toast.error(t("billing.failedToLoadTenantPaymentSettings"));
  };
  const handlePaymentToggleChange = (checked: boolean) => {
    updateTenantPaymentSettings.mutate(
      { paymentsEnabled: checked },
      {
        onError: handlePaymentToggleError,
      },
    );
  };
  const handlePartnersToggleChange = (checked: boolean) => {
    updateTenantPartnersSettings.mutate(
      { enabled: checked },
      {
        onError: handlePaymentToggleError,
      },
    );
  };

  if (!isUserLoading && !canReadAnySection) {
    return <Navigate replace to={getDefaultAuthorizedRoute(userProfile)} />;
  }

  return (
    <div className="flex flex-col gap-6 p-12">
      {isTenantAdmin && settingsAccess.canRead ? (
        <TenantProfileSection canWrite={settingsAccess.canWrite} />
      ) : null}

      {settingsAccess.canRead ? (
        <AdminPmsSettingsSection canWrite={settingsAccess.canWrite} />
      ) : null}

      {billingAccess.canRead ? (
        <SettingsToggleSection
          checked={Boolean(tenantPaymentSettings?.paymentsEnabled)}
          disabled={
            !billingAccess.canWrite || updateTenantPaymentSettings.isPending
          }
          isLoading={isLoading}
          isError={isError}
          loadingText={t("billing.loadingTenantPaymentSettings")}
          errorText={t("billing.failedToLoadTenantPaymentSettings")}
          onChange={handlePaymentToggleChange}
          renderToggle={(checked, disabled, onChange) => (
            <BillingEnabledRow
              checked={checked}
              disabled={disabled}
              onChange={onChange}
            />
          )}
        />
      ) : null}

      {partnersAccess.canRead ? (
        <SettingsToggleSection
          checked={Boolean(tenantPartnersSettings?.partnersEnabled)}
          disabled={
            !partnersAccess.canWrite || updateTenantPartnersSettings.isPending
          }
          isLoading={isPartnersLoading}
          isError={isPartnersError}
          loadingText={t("common.loading")}
          errorText={t("billing.failedToLoadTenantPaymentSettings")}
          onChange={handlePartnersToggleChange}
          renderToggle={(checked, disabled, onChange) => (
            <PartnersIntegrationEnabledRow
              checked={checked}
              disabled={disabled}
              onChange={onChange}
            />
          )}
        />
      ) : null}

      {isTenantAdmin && billingAccess.canRead ? (
        <TenantHotelsStripeSection canWrite={billingAccess.canWrite} />
      ) : null}
    </div>
  );
};
